import * as React from "react"
import { Box, Flex } from "@chakra-ui/react"
import { badgeHistoryListSelector } from "../../../recoil/atoms/badgeHistory"
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil"
import { ScrollMenu } from "react-horizontal-scrolling-menu"
import { LeftArrow } from "./leftArrow"
import { RightArrow } from "./rightArrow"
import BadgeOverlay from "../../servicesPanel/badges/_badgeOverlay"
import layoutStates from "../../../recoil/atoms/layout"
import { brandingSelector } from "../../../recoil/atoms/room"
import { FadeBadges } from "../../servicesPanel/badges/_fadeBadges"
import styled from "@emotion/styled"
import { viewDependingLayoutAtom } from "../../../recoil/atoms/view"
import { taggedBadgesListSelector } from "../../../recoil/atoms/taggedBadges"
import { shoppableMNPVideoAtom } from "../../../recoil/atoms/shoppableVideos"
import { useTranslation } from "react-i18next"
import useWindowSize from "../../../hooks/useWindowSize"

export interface BadgesLiveModeProps {
  width?: number
  isMobile?: boolean
  height?: number | string
  maxHeight?: number | string
  isSmallMainScreen?: boolean
  isTransperent?: boolean
  isRedirectToUrl?: boolean
  isTablet?: boolean
}

type PostionScrollType = "top" | "bottom" | "middle" | "noHasScrollbar"

interface StyledProps {
  positionscroll?: PostionScrollType
  maxWidthContainer?: number
}

const ScrollingMenuContainer = styled(Flex)<StyledProps>`
  .react-horizontal-scrolling-menu--wrapper {
    max-width: ${({ maxWidthContainer }) => `${maxWidthContainer}px`};
  }
`

const Container = styled(Flex)<StyledProps>`
  -webkit-align-content: end;
  align-content: end;
  --scrollbar-width-product-description: 1px;
  --mask-height-product-description: 30px;
  overflow-y: auto;
  --mask-size-content-product-description: calc(
      100% - var(--scrollbar-width-product-description)
    )
    100%;
  --mask-image-scrollbar-product-description: linear-gradient(black, black);
  --mask-size-scrollbar-product-description: var(
      --scrollbar-width-product-description
    )
    100%;
  mask-image: ${({ positionscroll }) =>
    positionscroll === "noHasScrollbar"
      ? ""
      : `linear-gradient(
    to ${positionscroll === "top" ? "top" : "bottom"},
    transparent,
    black var(--mask-height-product-description),
    black calc(100% - ${
      positionscroll === "middle"
        ? "var(--mask-height-product-description)"
        : "0px"
    }),
    transparent
  ),
  var(--mask-image-scrollbar-product-description)`};
  mask-size: var(--mask-size-content-product-description),
    var(--mask-size-scrollbar-product-description);
  mask-position: 0 0, 100% 0;
  mask-repeat: no-repeat, no-repeat;

  ::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`

const _BadgesLiveMode: React.FC<BadgesLiveModeProps> = ({
  width,
  isMobile,
  height,
  isSmallMainScreen,
  isTransperent,
  isRedirectToUrl,
  isTablet,
  maxHeight
}) => {
  const { t } = useTranslation()
  const { isLandscape } = useRecoilValue(viewDependingLayoutAtom)
  const allBadgeHistory = useRecoilValue(badgeHistoryListSelector)
  // const [, setPositionBadgesScroll] = useRecoilState(positionFsBadgeHistoryAtom)
  const { screenSize } = useWindowSize()
  const isSmallDesktopWidth = React.useMemo(() => width < 430, [width])
  const isSidePanelWithSingleTab = useRecoilValue(
    layoutStates.isSidePanelWithSingleTab
  )
  const isSidePanelOpen = useRecoilValue(layoutStates.openSidebar)

  const avoidCollapseBtn = isSidePanelWithSingleTab && !isSidePanelOpen

  const [, setFsInterfaceTab] = useRecoilState<string>(
    layoutStates.fsInterfaceTab
  )
  const taggedBadgesList = useRecoilValue(taggedBadgesListSelector)

  const roomStyles = useRecoilValue(brandingSelector)
  React.useEffect(() => {
    setFsInterfaceTab("badgesList")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isOverflowingContainer = React.useMemo(() => {
    return 290 * allBadgeHistory.length > width
  }, [allBadgeHistory.length, width])

  const [targetTabIndex] = useRecoilState<number>(
    layoutStates.historyTabEnabledMobile
  )
  const badgesContainerRef = React.useRef<HTMLDivElement>(null)
  const [positionScroll, setPositionScroll] =
    React.useState<PostionScrollType>("noHasScrollbar")

  const setHideFSScrollControlBadges = useSetRecoilState(
    layoutStates.hideFSScrollControlBadges
  )

  // UseEffect to add bottom or top shadow to list when container size change
  React.useEffect(() => {
    if (isMobile) {
      const badgesContainer = badgesContainerRef.current
      const observer = new ResizeObserver(entries => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for (let entry of entries) {
          if (badgesContainer.scrollHeight <= badgesContainer.clientHeight) {
            setHideFSScrollControlBadges(true)
            setPositionScroll("noHasScrollbar")
            // setPositionBadgesScroll("middle")
          } else {
            setHideFSScrollControlBadges(false)

            if (badgesContainer.scrollTop < 10) {
              setPositionScroll("top")
              // setPositionBadgesScroll("top")
            } else if (
              badgesContainer?.scrollTop >=
              badgesContainer?.scrollHeight - badgesContainer?.clientHeight - 10
            ) {
              setPositionScroll("bottom")
              // setPositionBadgesScroll("bottom")
            } else {
              setPositionScroll("middle")
              // setPositionBadgesScroll("middle")
            }
          }
        }
      })

      if (badgesContainer) {
        observer.observe(badgesContainer)
      }

      return () => {
        if (badgesContainer) {
          observer.unobserve(badgesContainer)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [badgesContainerRef, isTransperent, targetTabIndex])

  // UseEffect to add bottom or top shadow to list when user scroll
  React.useEffect(() => {
    if (isMobile) {
      const badgesContainer = badgesContainerRef.current

      if (!badgesContainer) return

      const handleScroll = () => {
        if (badgesContainer.scrollTop < 10) {
          setPositionScroll("top")
          // setPositionBadgesScroll("top")
        } else if (
          badgesContainer?.scrollTop >=
            badgesContainer?.scrollHeight -
              badgesContainer?.clientHeight -
              10 &&
          badgesContainer.scrollTop !== 0
        ) {
          setPositionScroll("bottom")
          // setPositionBadgesScroll("bottom")
        } else {
          if (badgesContainer.scrollTop !== 0) {
            setPositionScroll("middle")
            // setPositionBadgesScroll("middle")
          }
        }

        /* handleDisableControls("mobile-badges-list") */
      }

      badgesContainer.addEventListener("scroll", handleScroll, false)
      return () =>
        badgesContainer.removeEventListener("scroll", handleScroll, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTransperent, targetTabIndex])

  const [firstOpen, setFirstOpen] = React.useState(true)

  // const showInBottom = async () => {
  //   setFirstOpen(true)
  //   const container = (await waitForElm("mobile-badges-list")) as HTMLDivElement

  //   container.scrollTop = container.scrollHeight
  //   setFirstOpen(false)
  // }

  // React.useEffect(() => {
  //   if (isLandscape) {
  //     let timerForScroll = setTimeout(() => {
  //       showInBottom()
  //     }, 300)
  //     return () => clearTimeout(timerForScroll)
  //   }
  // }, [isLandscape])

  React.useEffect(() => {
    if (isMobile) {
      if (targetTabIndex === 1) {
        setFirstOpen(true)
      }

      // if (targetTabIndex === 2) {
      //   badgesContainerRef.current.scrollTop =
      //     badgesContainerRef.current.scrollHeight

      //   let timer = setTimeout(() => {
      //     setFirstOpen(false)
      //   }, 1000)
      //   return () => clearTimeout(timer)
      // }
    }
  }, [targetTabIndex, isTransperent, isLandscape, isMobile])

  const { enabledShoppableMNP } = useRecoilValue(shoppableMNPVideoAtom)

  const desktopBadgesHistory = React.useMemo(() => {
    return enabledShoppableMNP ? taggedBadgesList : allBadgeHistory
  }, [allBadgeHistory, enabledShoppableMNP, taggedBadgesList])

  const mobileBadgesHistory = React.useMemo(() => {
    if (enabledShoppableMNP) {
      return taggedBadgesList
    }
    return allBadgeHistory
  }, [allBadgeHistory, enabledShoppableMNP, taggedBadgesList])

  if (isMobile) {
    return (
      <Flex
        flexDir="column"
        alignItems="center"
        overflowY="auto"
        padding={isLandscape || isTransperent ? "0px" : "10px 0px"}
        height={height}
        maxHeight={maxHeight}
        // bg={!isTransperent && (roomStyles?.servicePanelColor ?? "#F7FAFC")}
      >
        <Container
          id="mobile-badges-list"
          flexDir="column"
          alignItems="center"
          overflowY="auto"
          maxWidth={screenSize.width}
          overflowX="hidden"
          ref={badgesContainerRef}
          positionscroll={positionScroll}
          style={firstOpen ? {} : { scrollBehavior: "smooth" }}
        >
          {allBadgeHistory.length === 0 &&
            !isTransperent &&
            !enabledShoppableMNP && (
              <Box width={width}>
                {" "}
                <FadeBadges
                  isSmallView={true}
                  infoText={t(
                    "Featured products shown here are those items that you have shown as video overlay badges via the LiveSell tab. Audience members have a single <i>Shop</i> tab which will display these Featured products, or Tagged products, if they are watching a shoppable video which has had products tagged to it."
                  )}
                />{" "}
              </Box>
            )}
          {mobileBadgesHistory?.map((singleBadgeData, index) => (
            <Box
              zIndex="popover"
              display="block"
              flexDirection="column"
              width={width}
              marginBottom="10px"
              key={`BadgeHistory-${singleBadgeData.id}`}
            >
              <BadgeOverlay
                {...singleBadgeData}
                width={width}
                isSmallMainScreen={isSmallMainScreen}
                isMobile={isMobile}
                isTouch={!isTablet}
                isRedirectToUrl={isRedirectToUrl}
                isBadgeLiveMode={true}
                isTransperent={isTransperent}
              />
            </Box>
          ))}
        </Container>
      </Flex>
    )
  }

  return (
    <Box
      width={width}
      height="fit-content"
      padding={
        isSmallDesktopWidth
          ? "0.5rem 0.5rem 0rem 0.5rem"
          : "1rem 0rem 0rem 0rem"
      }
      pos="relative"
      overflowY={isSmallDesktopWidth ? "auto" : "unset"}
      marginTop={isSmallDesktopWidth && "10px"}
      flex={isSmallDesktopWidth && 1}
      display={isOverflowingContainer ? "block" : "flex"}
      justifyContent="center"
    >
      {isSmallDesktopWidth ? (
        <Flex flexDirection="column" overflow="auto">
          {desktopBadgesHistory?.map(singleBadgeData => (
            <Box
              width={"100%"}
              minHeight={"30px"}
              height="auto"
              display="flex"
              flexDirection="row"
              zIndex="popover"
              p="0"
              m="0"
              left="0"
              marginLeft="0px"
              itemId={singleBadgeData.id}
              marginBottom="10px"
              key={`BadgeHistory-${singleBadgeData.id}`}
            >
              <BadgeOverlay
                {...singleBadgeData}
                // width={width}
                isSmallMainScreen={isSmallMainScreen}
                isMobile={isMobile}
                isBadgeLiveMode={true}
              />
            </Box>
          ))}
        </Flex>
      ) : (
        <ScrollingMenuContainer
          id="desktop-blm"
          maxWidthContainer={avoidCollapseBtn ? width - 50 : width}
          style={{
            display: "flex",
            maxWidth: width,
            justifyContent: "center",
            padding: "0rem 3rem"
          }}
          options={{ throttle: 0 }}
        >
          <ScrollMenu
            LeftArrow={
              <LeftArrow left={avoidCollapseBtn ? "32px" : undefined} />
            }
            RightArrow={
              <RightArrow right={avoidCollapseBtn ? "32px" : undefined} />
            }
          >
            {desktopBadgesHistory?.map((singleBadgeData, index) => (
              <Box
                minWidth={"280px"}
                maxWidth={"400px"}
                minHeight={"30px"}
                height="100%"
                bottom={"15px"}
                display="flex"
                flexDirection="row"
                zIndex="popover"
                p="0"
                m="0"
                left="0"
                marginLeft="0px"
                itemId={singleBadgeData.id}
                marginRight={
                  index === desktopBadgesHistory.length - 1 ? "0px" : "10px"
                }
                key={`BadgeHistory-${singleBadgeData.id}`}
              >
                <BadgeOverlay
                  {...singleBadgeData}
                  // width={width}
                  isSmallMainScreen={isSmallMainScreen}
                  isMobile={isMobile}
                  isBadgeLiveMode={true}
                />
              </Box>
            ))}
          </ScrollMenu>
        </ScrollingMenuContainer>
      )}
    </Box>
  )
}

export const BadgesLiveMode = React.memo(_BadgesLiveMode)
